/**
 * features/parent/parentPortalControls.tsx
 * ------------------------------------------------------------------
 * Admin-controlled, DISPLAY-ONLY maintenance controls for the parent
 * portal, driven by schoolSettings/default:
 *   - parentNotice          — a banner across parent surfaces
 *   - parentFeesVisible      — hide fee balances/statement
 *   - parentPortalMaintenance — a full "under maintenance" screen
 *
 * IMPORTANT: these are purely cosmetic. They deliberately do NOT read or
 * write the fee-arrears academic hold (financeAcademicHolds /
 * familyHasArrears / useParentAcademicHold). A student on an arrears hold
 * stays held regardless of any of these flags; turning a flag on or off
 * never grants or removes academic access. Keep it that way.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { Megaphone, Wrench } from 'lucide-react';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { GradientHero, SoftCard } from '@/features/finance/ui';
import type { SchoolSettings } from '@/types';

export interface ParentPortalControls {
  notice: { enabled: boolean; message: string };
  feesVisible: boolean;
  maintenance: { enabled: boolean; message: string };
  ready: boolean;
}

/** Live subscription to the parent-portal display controls. */
export function useParentPortalControls(): ParentPortalControls {
  const [s, setS] = useState<SchoolSettings | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, COLLECTIONS.schoolSettings, 'default'),
      (snap) => { setS(snap.exists() ? (snap.data() as SchoolSettings) : null); setReady(true); },
      () => setReady(true),
    );
    return () => unsub();
  }, []);
  return {
    notice: { enabled: !!s?.parentNotice?.enabled, message: s?.parentNotice?.message || '' },
    feesVisible: s?.parentFeesVisible !== false,
    maintenance: { enabled: !!s?.parentPortalMaintenance?.enabled, message: s?.parentPortalMaintenance?.message || '' },
    ready,
  };
}

const DEFAULT_MAINT = 'The parent portal is briefly unavailable while we carry out maintenance. Please check back shortly.';

/** The school notice banner. Renders nothing if there's no message. */
export function ParentNoticeBanner({ message }: { message: string }) {
  if (!message.trim()) return null;
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/50 px-4 py-3 flex items-start gap-3">
      <Megaphone size={18} className="text-amber-600 dark:text-amber-300 mt-0.5 shrink-0" />
      <p className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed whitespace-pre-line">{message}</p>
    </div>
  );
}

/** Full-surface maintenance screen (parents see only this). */
export function ParentMaintenanceScreen({ message }: { message: string }) {
  return (
    <div className="space-y-5">
      <GradientHero icon={Wrench} title="Under maintenance" subtitle="We'll be back shortly" />
      <SoftCard>
        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
          {message.trim() || DEFAULT_MAINT}
        </p>
      </SoftCard>
    </div>
  );
}
